<script setup lang="ts">
const { locale } = useI18n()

const { data } = await useFetch('/api/weather')

const wmoIcon = (code: number): string => {
  if (code === 0) return 'ph:sun'
  if (code <= 3) return 'ph:cloud-sun'
  if (code <= 48) return 'ph:cloud-fog'
  if (code <= 67) return 'ph:cloud-rain'
  if (code <= 77) return 'ph:cloud-snow'
  if (code <= 82) return 'ph:cloud-rain'
  if (code <= 86) return 'ph:cloud-snow'
  return 'ph:cloud-lightning'
}

const toCompass = (deg: number): string => {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  return dirs[Math.round(deg / 45) % 8] ?? ""
}

const dayName = (dateStr: string): string =>
    new Intl.DateTimeFormat(locale.value, { weekday: 'short' }).format(new Date(dateStr))
</script>

<template>
  <div v-if="data" class="weather-widget">
    <div class="current">
      <Icon :name="wmoIcon(data.current.weather_code)" class="cond-icon" />
      <span class="temp">{{ Math.round(data.current.temperature_2m) }}°</span>
      <span class="detail">{{ toCompass(data.current.wind_direction_10m) }} {{ Math.round(data.current.wind_speed_10m) }} km/h</span>
      <span class="sep-dot">·</span>
      <span class="detail">{{ data.current.precipitation }} mm</span>
    </div>

    <div class="divider" />

    <div class="forecast">
      <div v-for="(_, i) in data.daily.time" :key="i" class="day">
        <span class="day-name">{{ dayName(data.daily.time[i]) }}</span>
        <Icon :name="wmoIcon(data.daily.weather_code[i])" class="day-icon" />
        <span class="day-temps">
          <strong>{{ Math.round(data.daily.temperature_2m_max[i]) }}°</strong>
          <span class="min-temp">{{ Math.round(data.daily.temperature_2m_min[i]) }}°</span>
        </span>
      </div>
    </div>
  </div>

  <div v-else class="weather-widget weather-widget--error" />
</template>

<style scoped lang="sass">
.weather-widget
  display: flex
  align-items: center
  gap: 1.25rem
  padding: 1.1rem 1.5rem
  background: #f4f4f4
  border-radius: $border-radius-card
  box-shadow: $shadow-hover
  width: 60%
  flex-wrap: nowrap

.dark-mode .weather-widget
  background: $color-surface-dark

.weather-widget--error
  height: 3rem
  opacity: 0.3

.current
  display: flex
  align-items: center
  gap: 0.5rem
  flex-shrink: 0

.cond-icon
  font-size: 2.25rem
  color: $color-primary

.temp
  font-size: 1.25rem
  font-weight: 800
  color: $color-primary
  font-family: 'Montserrat', sans-serif

.detail
  font-size: 0.8rem
  color: rgba(0, 0, 0, 0.7)
  white-space: nowrap

.dark-mode .detail
  color: rgba(255, 255, 255, 0.5)

.sep-dot
  color: rgba(0, 0, 0, 0.4)
  font-size: 0.8rem

.dark-mode .sep-dot
  color: rgba(255, 255, 255, 0.2)

.divider
  width: 1px
  height: 2rem
  background: rgba(0, 0, 0, 0.2)
  flex-shrink: 0

.dark-mode .divider
  background: rgba(255, 255, 255, 0.1)

.forecast
  display: flex
  gap: 0.35rem
  align-items: center
  flex: 1
  justify-content: space-between

.day
  display: flex
  flex-direction: column
  align-items: center
  gap: 0.15rem

.day-name
  font-size: 0.7rem
  font-weight: 700
  text-transform: uppercase
  letter-spacing: 0.5px
  color: rgba(0, 0, 0, 0.6)

.dark-mode .day-name
  color: rgba(255, 255, 255, 0.35)

.day-icon
  font-size: 1.75rem
  color: $color-primary

.day-temps
  display: flex
  gap: 0.25rem
  font-size: 0.78rem
  font-weight: 600

  strong
    color: rgba(0, 0, 0, 0.8)

.dark-mode .day-temps strong
  color: rgba(255, 255, 255, 0.85)

.min-temp
  color: rgba(0, 0, 0, 0.5)

.dark-mode .min-temp
  color: rgba(255, 255, 255, 0.3)

@media (max-width: 900px)
  .weather-widget
    width: 100%

@media (max-width: 480px)
  .weather-widget
    flex-wrap: wrap
    gap: 0.75rem 0

  .current
    width: 100%

  .divider
    display: none

  .forecast
    width: 100%
    gap: 0
</style>